import React from 'react'
import { useNavigate } from 'react-router-dom'
import { cardStyle, accent, accentLight, textPrimary, bgMuted } from './styles'
import { getHomeUrl } from './gameLogic'

const HowToPlayPage = () => {
  const navigate = useNavigate()

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgMuted, padding: '20px 16px 40px' }}>
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        ...cardStyle,
        padding: '32px 24px',
      }}>
        {/* Back Button */}
        <button onClick={() => window.location.href = getHomeUrl()} style={{
          background: 'none',
          border: 'none',
          color: accent,
          cursor: 'pointer',
          fontSize: '0.9rem',
          fontWeight: '700',
          marginBottom: '20px',
          textDecoration: 'underline',
          padding: 0,
        }}>
          Home
        </button>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            backgroundColor: accent,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem',
            fontWeight: '900',
            color: 'white',
          }}>
            E
          </div>
          <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: '900', letterSpacing: '-0.03em' }}>
            <span style={{ color: textPrimary }}>Event</span>
            <span style={{ color: accent }}>Pro</span>
          </h1>
        </div>

        <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '28px', fontWeight: '500' }}>
          How to play
        </p>

        {/* The Objective */}
        <div style={{ marginBottom: '32px' }}>
          <p style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0a0a0a', marginBottom: '14px', marginTop: '0' }}>
            🎯 The Objective
          </p>
          <p style={{ fontSize: '0.95rem', color: '#555', lineHeight: '1.6', marginBottom: '0' }}>
            Place famous pop-culture events in chronological order, from movie and music milestones to moments in the lives and careers of iconic performers and athletes. As you place events, the timeline grows and reveals the years of your previous placements.
          </p>
        </div>

        {/* Rules */}
        <div style={{ marginBottom: '32px' }}>
          <p style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0a0a0a', marginBottom: '14px', marginTop: '0' }}>
            📋 Rules
          </p>
          <ul style={{ fontSize: '0.95rem', color: '#333', paddingLeft: '20px', margin: '0' }}>
            <li style={{ marginBottom: '8px' }}>You can play once per day</li>
            <li style={{ marginBottom: '8px' }}>Your wager must be between $10 and $50 (or up to your weekly bank on Sundays)</li>
            <li style={{ marginBottom: '8px' }}>All players play the same daily Pop Culture puzzle and anchor event</li>
            <li style={{ marginBottom: '0' }}>Your weekly bank resets at the start of each new week</li>
          </ul>
        </div>

        {/* How It Works */}
        <div style={{ marginBottom: '32px' }}>
          <p style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0a0a0a', marginBottom: '14px', marginTop: '0' }}>
            ⚙️ How It Works
          </p>
          <ol style={{ fontSize: '0.95rem', color: '#333', paddingLeft: '20px', margin: '0' }}>
            <li style={{ marginBottom: '10px' }}>
              <strong>Anchor Year:</strong> You see one Pop Culture event with its year already revealed
            </li>
            <li style={{ marginBottom: '10px' }}>
              <strong>Place Events:</strong> Each turn, a new event appears with its year hidden. You choose where to place it on the timeline
            </li>
            <li style={{ marginBottom: '10px' }}>
              <strong>Build the Timeline:</strong> As you place events, they join the timeline. Correct placements show green; wrong placements show red with a strike
            </li>
            <li style={{ marginBottom: '0' }}>
              <strong>Score:</strong> Your accuracy (0/4 to 4/4 correct) determines your daily win or loss on your wager
            </li>
          </ol>
        </div>

        {/* The Wager System */}
        <div style={{ marginBottom: '32px' }}>
          <p style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0a0a0a', marginBottom: '14px', marginTop: '0' }}>
            💰 The Wager System
          </p>
          <p style={{ fontSize: '0.95rem', color: '#555', lineHeight: '1.6', marginBottom: '12px' }}>
            Before playing, you wager an amount between $10 and $50. Your daily result is multiplied by your wager on normal days:
          </p>
          <ul style={{ fontSize: '0.95rem', color: '#333', paddingLeft: '20px', margin: '0' }}>
            <li style={{ marginBottom: '8px' }}><strong>4/4 (Perfect):</strong> Win 1× your wager</li>
            <li style={{ marginBottom: '8px' }}><strong>3/4:</strong> Win 0.5× your wager</li>
            <li style={{ marginBottom: '8px' }}><strong>2/4 (Push):</strong> Break even (no gain or loss)</li>
            <li style={{ marginBottom: '8px' }}><strong>1/4:</strong> Lose 0.5× your wager</li>
            <li style={{ marginBottom: '0' }}><strong>0/4 (Bust):</strong> Lose 1× your wager</li>
          </ul>
        </div>

        {/* Placement Zones */}
        <div style={{ marginBottom: '32px' }}>
          <p style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0a0a0a', marginBottom: '14px', marginTop: '0' }}>
            📍 How to Place Events
          </p>
          <p style={{ fontSize: '0.95rem', color: '#555', lineHeight: '1.6', marginBottom: '12px' }}>
            When placing an event, you'll see placement zones (dashed lines) between events on the timeline:
          </p>
          <ul style={{ fontSize: '0.95rem', color: '#333', paddingLeft: '20px', margin: '0' }}>
            <li style={{ marginBottom: '0' }}>Tap a zone to highlight it, then tap [PLACE] to lock in your selection</li>
          </ul>
        </div>

        {/* Reveals and Scoring */}
        <div style={{ marginBottom: '32px' }}>
          <p style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0a0a0a', marginBottom: '14px', marginTop: '0' }}>
            🔍 Reveals and Scoring
          </p>
          <p style={{ fontSize: '0.95rem', color: '#555', lineHeight: '1.6', marginBottom: '12px' }}>
            After all 4 events are placed:
          </p>
          <ul style={{ fontSize: '0.95rem', color: '#333', paddingLeft: '20px', margin: '0' }}>
            <li style={{ marginBottom: '8px' }}>The timeline reveals all event years one by one</li>
            <li style={{ marginBottom: '8px' }}>Correct placements show 🟢; incorrect ones show 🔴</li>
            <li style={{ marginBottom: '0' }}>Your final score and earnings display, and are added to your weekly bank</li>
          </ul>
        </div>

        {/* Sunday Double */}
        <div style={{ marginBottom: '32px', padding: '16px', backgroundColor: accentLight, borderRadius: '12px', borderLeft: `4px solid ${accent}` }}>
          <p style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0a0a0a', marginBottom: '14px', marginTop: '0' }}>
            ⚡ Sunday Double or Nothing
          </p>
          <p style={{ fontSize: '0.95rem', color: '#555', lineHeight: '1.6', marginBottom: '0' }}>
            On Sundays, the stakes double! You can wager up to your entire weekly bank, and all wins and losses are 2×. Use this as a chance to grow your bank or risk it all.
          </p>
        </div>

        {/* Leaderboard */}
        <div style={{ marginBottom: '32px' }}>
          <p style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0a0a0a', marginBottom: '14px', marginTop: '0' }}>
            🏆 Leaderboard Ranking
          </p>
          <p style={{ fontSize: '0.95rem', color: '#555', lineHeight: '1.6', marginBottom: '12px' }}>
            Unlike other games, EventPro's leaderboard ranks players by money:
          </p>
          <ul style={{ fontSize: '0.95rem', color: '#333', paddingLeft: '20px', margin: '0' }}>
            <li style={{ marginBottom: '8px' }}><strong>Daily Leaderboard:</strong> Ranked by daily payout (highest first)</li>
            <li style={{ marginBottom: '8px' }}><strong>Weekly Bank:</strong> Your accumulated earnings for the current week</li>
            <li style={{ marginBottom: '0' }}>Equal payouts or bank totals share a rank (for example, T1, T1, 3).</li>
          </ul>
        </div>

        {/* Tips */}
        <div style={{ marginBottom: '32px' }}>
          <p style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0a0a0a', marginBottom: '14px', marginTop: '0' }}>
            🧠 Strategy Tips
          </p>
          <ul style={{ fontSize: '0.95rem', color: '#333', paddingLeft: '20px', margin: '0' }}>
            <li style={{ marginBottom: '8px' }}>Start with a comfortable wager ($10–$20) while learning the game</li>
            <li style={{ marginBottom: '8px' }}>Think about relative dates: Is this event before or after the anchor? Before or after other events?</li>
            <li style={{ marginBottom: '8px' }}>Remember: one strike (wrong placement) costs you a point, so accuracy matters more than guessing</li>
            <li style={{ marginBottom: '8px' }}>Use higher wagers on days you feel confident, and lower wagers when uncertain</li>
            <li style={{ marginBottom: '0' }}>On Sundays, consider your weekly bank before wagering the max</li>
          </ul>
        </div>

        {/* Back Link */}
        <div style={{ textAlign: 'center' }}>
          <button onClick={() => window.location.href = getHomeUrl()} style={{
            padding: '12px 28px',
            backgroundColor: accent,
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '700',
            fontSize: '0.95rem',
            cursor: 'pointer',
            boxShadow: `0 2px 0 #c2410c`,
          }}>
            Home
          </button>
        </div>
      </div>
    </div>
  )
}

export default HowToPlayPage
